import { useState } from "react";
import api, { API } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Download } from "lucide-react";

const token = () => localStorage.getItem("contaha_token");
export const eduSrc = (r, download) => `${API}/education/resources/${r.id}/content?auth=${token()}${download ? "&download=1" : ""}`;
const kindOf = (r) => (r.type === "link" ? r.display || "page" : r.type);
const block = (e) => e.preventDefault();

// Upload in 8 MB pieces; resumes from pieces already received.
export async function chunkedUpload(file, folder, onProgress) {
  const key = `edu-upload-${file.name}-${file.size}-${folder.id}`;
  let up = null;
  const saved = localStorage.getItem(key);
  if (saved) { up = await api.get(`/education/uploads/${saved}`).then((r) => r.data).catch(() => null); }
  if (!up) {
    up = (await api.post("/education/uploads", { file_name: file.name, size: file.size, folder_id: folder.id, condition_slug: folder.condition_slug || "" })).data;
    localStorage.setItem(key, up.id);
  }
  const done = new Set(up.received || []);
  for (let i = 0; i < up.chunks; i += 1) {
    if (!done.has(i)) {
      const body = file.slice(i * up.chunk_size, (i + 1) * up.chunk_size);
      for (let attempt = 0; ; attempt += 1) {
        try { await api.put(`/education/uploads/${up.id}/chunk/${i}`, body, { headers: { "Content-Type": "application/octet-stream" } }); break; }
        catch (e) { if (attempt >= 4) throw e; await new Promise((r) => setTimeout(r, 1500 * (attempt + 1))); }
      }
      done.add(i);
    }
    onProgress(Math.round((done.size / up.chunks) * 100));
  }
  const { data } = await api.post(`/education/uploads/${up.id}/complete`, { title: file.name });
  localStorage.removeItem(key);
  return data;
}

export function EduViewer({ r, canDownload, onClose }) {
  const [err, setErr] = useState(false);
  if (!r) return null;
  const k = kindOf(r);
  const yt = r.type === "link" && (r.url || "").match(/(?:youtube\.com\/(?:watch\?(?:.*&)?v=|shorts\/|embed\/)|youtu\.be\/)([\w-]{6,})/);
  const src = yt ? `https://www.youtube-nocookie.com/embed/${yt[1]}` : r.type === "link" ? r.url : eduSrc(r);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[94vh] max-w-5xl overflow-y-auto" data-testid="edu-viewer" onContextMenu={block}>
        <DialogHeader><DialogTitle className="break-words">{r.title}</DialogTitle><DialogDescription>{r.type === "link" ? "Hosted externally · viewed inside CONTAHA" : "View only inside CONTAHA"}</DialogDescription></DialogHeader>
        <div className="select-none" onContextMenu={block} onDragStart={block}>
          {k === "video" && <video src={src} controls controlsList="nodownload noplaybackrate" disablePictureInPicture className="w-full rounded-lg bg-black" data-testid="edu-viewer-video" onError={() => setErr(true)} />}
          {k === "image" && <img src={src} alt={r.title} className="w-full rounded-lg" draggable={false} data-testid="edu-viewer-image" />}
          {(k === "pdf" || k === "page") && <iframe title={r.title} allow="autoplay; encrypted-media; picture-in-picture; fullscreen" allowFullScreen src={k === "pdf" ? `${src}#toolbar=0&navpanes=0` : src} className="h-[70vh] w-full rounded-lg border border-[#E2E8F0]" data-testid="edu-viewer-frame" />}
          {k === "file" && <p className="text-[#8B9698]">Preview isn't available for this file type.</p>}
          {err && <p className="mt-2 text-amber-700">This video couldn't be played here.</p>}
        </div>
        {canDownload && r.type !== "link" && <a href={eduSrc(r, true)} data-testid="edu-viewer-download"><Button variant="outline"><Download className="mr-1.5 h-4 w-4" />Download</Button></a>}
      </DialogContent>
    </Dialog>
  );
}
